declare global {
  interface Window {
    dtrum?: {
      reportError: (error: string | Error) => void;
      endSession: () => void;
      disable?: () => void;
      enable?: () => void;
      sendSessionProperties: (undefined: undefined, dates: Record<string, Date>, strings: Record<string, string>, numbers: Record<string, number>) => void;
      enterAction: (actionName: string) => string | undefined;
      addActionProperties: (actionId: string, undefined: undefined, dates: Record<string, Date>, strings: Record<string, string>, numbers: Record<string, number>) => void; 
      leaveAction: (actionId: string) => void;
      identifyUser: (userId: string) => void;
    };
  }
}

const getDynatraceRum = () => globalThis.window?.dtrum;

export const injectDynatraceSnippet = (snippet: string) => {
  if (!snippet) return false;
  
  const existingScript = document.querySelector('script[src*="dynatrace"], script[data-dtconfig]');
  if (existingScript) {
    return true;
  }
  
  const temp = document.createElement("div");
  temp.innerHTML = snippet.trim();
  const script = temp.querySelector("script");
  if (!script) return false;
  const newScript = document.createElement("script");
  if (script.src) {
    newScript.src = script.src;
  } else {
    newScript.textContent = script.textContent;
  }
  Array.from(script.attributes).forEach(attr => {
    newScript.setAttribute(attr.name, attr.value);
  });
  document.head.appendChild(newScript);
  return true;
};

export const initializeDynatrace = () => {
  const dynatraceUrl = process.env.NEXT_PUBLIC_DYNATRACE_URL;
  if (!dynatraceUrl) {
    return false;
  }

  const script = document.createElement("script");
  script.id = "dynatrace";
  script.type = "text/javascript";
  script.crossOrigin = "anonymous";
  script.src = dynatraceUrl; 
  document.head.appendChild(script);
  return true;
};

const splitProperties = (properties: Record<string, unknown>) => {
  const dates: Record<string, Date> = {};
  const strings: Record<string, string> = {};
  const numbers: Record<string, number> = {};

  Object.entries(properties).forEach((entry) => {
    const key = entry[0];
    const value = entry[1];

    if (value instanceof Date) {
      dates[key] = value;
    } else if (typeof value === "number") {
      numbers[key] = value;
    } else if (typeof value === "string") {
      strings[key] = value;
    }
  });

  return {
    dates,
    strings,
    numbers
  }
}

export const endDynatraceSession = () => {
  const dtrum = getDynatraceRum();
  if (!dtrum) {
    return;
  }

  dtrum.endSession();
};

export const disableDynatrace = () => {
  const dtrum = getDynatraceRum();
  if (!dtrum?.disable) return false;

  dtrum.disable();
  return true;
};

export const enableDynatrace = () => {
  const dtrum = getDynatraceRum();
  if (!dtrum?.enable) return false;

  dtrum.enable();
  return true;
};

export const trackDynatraceSession = (properties: Record<string, unknown>) => {
  const dtrum = getDynatraceRum();
  if (!dtrum) {
    return;
  }

  const {
    dates,
    strings,
    numbers,
  } = splitProperties(properties);

  dtrum.sendSessionProperties(undefined, dates, strings, numbers);
};

export const trackDynatraceAction = (
  actionName: string,
  properties?: Record<string, unknown>,
) => {
  const dtrum = getDynatraceRum();
  if (!dtrum) {
    return;
  }

  const actionId = dtrum.enterAction(actionName);
  if (actionId === undefined) {
    return;
  }

  if (properties) {
    const {
      dates,
      strings,
      numbers,
    } = splitProperties(properties);

    dtrum.addActionProperties(
      actionId,
      undefined,
      dates,
      strings,
      numbers,
    );
  }

  dtrum.leaveAction(actionId);
};

export const trackDynatraceError = (error: unknown) => {
  const dtrum = getDynatraceRum();
  if (!dtrum) {
    return;
  }

  if (typeof error === "string" || error instanceof Error) {
    dtrum.reportError(error);
  }
}

export const trackDynatraceUser = (userId: string) => {
  const dtrum = getDynatraceRum();
  if (!dtrum) {
    return;
  }

  dtrum.identifyUser(userId);
};
